import { Fragment, useState } from "react";

import { currency, EmptyRow } from "../App";
import type {
  ChargeRow,
  ChargeSort,
  DashboardResponse,
} from "../../shared/contracts";
import { toCsv, type CsvCell } from "../csv";
import { ChevronIcon, ExpandIcon } from "../icons";

function amount(value: number): string {
  if (value === 0) return "0";
  return Math.abs(value) < 0.01
    ? value.toExponential(2)
    : value.toLocaleString("en-US", { maximumFractionDigits: 4 });
}

/**
 * A delivery written before the usage columns were added to the export carries
 * neither an amount nor a unit. Rendering that as "0" would claim zero usage, so
 * the absent case reads as absent.
 */
export function usage(
  row: Pick<ChargeRow, "usageAmount" | "pricingUnit">,
): string {
  if (row.pricingUnit === "" && row.usageAmount === 0) return "—";
  return row.pricingUnit
    ? `${amount(row.usageAmount)} ${row.pricingUnit}`
    : amount(row.usageAmount);
}

function chargeAsk(row: ChargeRow, currencyCode: string): string {
  const base =
    `Charges row — date ${row.date}, service ${row.service}, usage type ${row.usageType}, ` +
    `charge type ${row.chargeType}, usage ${usage(row)}, unblended ${currency(row.cost, currencyCode)}, ` +
    `amortized ${currency(row.amortizedCost, currencyCode)}, account ${row.account}, region ${row.region}`;
  return row.chargeDetail ? `${base}, description ${row.chargeDetail}` : base;
}

export function chargesCsv(rows: ChargeRow[]): string {
  return toCsv(
    [
      "Date",
      "Service",
      "Account",
      "Region",
      "Usage type",
      "Charge type",
      "Description",
      "Usage amount",
      "Usage unit",
      "Resource",
      "Unblended",
      "Amortized",
    ],
    rows.map((row): CsvCell[] => [
      row.date,
      row.service,
      row.account,
      row.region,
      row.usageType,
      row.chargeType,
      row.chargeDetail,
      row.usageAmount,
      row.pricingUnit,
      row.resource,
      row.cost,
      row.amortizedCost,
    ]),
  );
}

function dataSize(bytes: number): string {
  if (bytes < 1_000_000) return `${(bytes / 1_000).toFixed(1)} KB`;
  if (bytes < 1_000_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  return `${(bytes / 1_000_000_000).toFixed(2)} GB`;
}

const inlineColumns: Array<{
  key: ChargeSort["key"];
  label: string;
}> = [
  { key: "date", label: "Date" },
  { key: "service", label: "Service" },
  { key: "usageType", label: "Usage type" },
  { key: "usageAmount", label: "Usage" },
  { key: "cost", label: "Unblended" },
  { key: "amortizedCost", label: "Amortized" },
];

const fullColumns: Array<{
  key: ChargeSort["key"] | null;
  label: string;
}> = [
  { key: "date", label: "Date" },
  { key: "service", label: "Service" },
  { key: null, label: "Account" },
  { key: null, label: "Region" },
  { key: "usageType", label: "Usage type" },
  { key: "chargeType", label: "Charge type" },
  { key: null, label: "Description" },
  { key: "usageAmount", label: "Usage" },
  { key: "cost", label: "Unblended" },
  { key: "amortizedCost", label: "Amortized" },
];

function SortHeader({
  column,
  sort,
  onSort,
}: {
  column: { key: ChargeSort["key"]; label: string };
  sort: ChargeSort;
  onSort: (key: ChargeSort["key"]) => void;
}) {
  return (
    <th
      aria-sort={
        sort.key === column.key
          ? sort.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <button
        className="sort-button"
        type="button"
        onClick={() => onSort(column.key)}
      >
        {column.label}
        <span aria-hidden="true">
          {sort.key === column.key
            ? sort.direction === "asc"
              ? " ▲"
              : " ▼"
            : ""}
        </span>
      </button>
    </th>
  );
}

export function ChargesTable({
  charges,
  currencyCode,
  sort,
  onSort,
  variant,
}: {
  charges: ChargeRow[];
  currencyCode: string;
  sort: ChargeSort;
  onSort: (key: ChargeSort["key"]) => void;
  variant: "inline" | "full";
}) {
  const [priorCharges, setPriorCharges] = useState(charges);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  // Sorting or filtering replaces the rows wholesale; stale expansion state
  // would attach to rows that no longer exist. Adjusted during render (rather
  // than an effect) so the stale set never paints.
  if (charges !== priorCharges) {
    setPriorCharges(charges);
    setExpanded(new Set());
  }
  const toggle = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (variant === "full") {
    return (
      <div className="table-wrap">
        <table>
          <caption className="sr-only">All CUR line items</caption>
          <thead>
            <tr>
              {fullColumns.map((column) =>
                column.key ? (
                  <SortHeader
                    key={column.label}
                    column={{ key: column.key, label: column.label }}
                    sort={sort}
                    onSort={onSort}
                  />
                ) : (
                  <th key={column.label}>{column.label}</th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {charges.length === 0 ? (
              <EmptyRow span={10} message="No line items for this range yet." />
            ) : (
              charges.map((row) => (
                <tr key={row.id} data-ask={chargeAsk(row, currencyCode)}>
                  <td className="number">{row.date}</td>
                  <td>{row.service}</td>
                  <td className="number">{row.account}</td>
                  <td className="number">{row.region}</td>
                  <td>{row.usageType}</td>
                  <td>{row.chargeType}</td>
                  <td>{row.chargeDetail || "—"}</td>
                  <td className="number">{usage(row)}</td>
                  <td className="number">{currency(row.cost, currencyCode)}</td>
                  <td className="number">
                    {currency(row.amortizedCost, currencyCode)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table>
        <caption className="sr-only">
          CUR line items under the active filters
        </caption>
        <thead>
          <tr>
            <th className="charges__toggle-cell" />
            {inlineColumns.map((column) => (
              <SortHeader
                key={column.key}
                column={column}
                sort={sort}
                onSort={onSort}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {charges.length === 0 ? (
            <EmptyRow span={7} message="No line items for this range yet." />
          ) : (
            charges.map((row) => (
              <Fragment key={row.id}>
                <tr data-ask={chargeAsk(row, currencyCode)}>
                  <td className="charges__toggle-cell">
                    <button
                      className="icon-button icon-button--row"
                      type="button"
                      onClick={() => toggle(row.id)}
                      aria-expanded={expanded.has(row.id)}
                      aria-label={`Details for ${row.service} on ${row.date}`}
                    >
                      <ChevronIcon size={14} />
                    </button>
                  </td>
                  <td className="number">{row.date}</td>
                  <td title={row.resource || undefined}>{row.service}</td>
                  <td title={row.chargeDetail || undefined}>{row.usageType}</td>
                  <td className="number">{usage(row)}</td>
                  <td className="number">{currency(row.cost, currencyCode)}</td>
                  <td className="number">
                    {currency(row.amortizedCost, currencyCode)}
                  </td>
                </tr>
                {expanded.has(row.id) && (
                  <tr className="charges__detail">
                    <td colSpan={7}>
                      <dl className="charges__detail-grid">
                        <div>
                          <dt>Account</dt>
                          <dd className="number">{row.account}</dd>
                        </div>
                        <div>
                          <dt>Region</dt>
                          <dd className="number">{row.region}</dd>
                        </div>
                        <div>
                          <dt>Charge type</dt>
                          <dd>{row.chargeType}</dd>
                        </div>
                        <div>
                          <dt>Description</dt>
                          <dd>{row.chargeDetail || "—"}</dd>
                        </div>
                        <div>
                          <dt>Resource</dt>
                          <dd>{row.resource || "—"}</dd>
                        </div>
                      </dl>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function ChargesCard({
  data,
  sort,
  onSort,
  onExport,
  onExpand,
}: {
  data: DashboardResponse;
  sort: ChargeSort;
  onSort: (key: ChargeSort["key"]) => void;
  onExport: () => void;
  onExpand: () => void;
}) {
  return (
    <section className="card" id="charges" aria-labelledby="charges-heading">
      <div
        className="card__header card__header--wrap"
        data-ask={`Charges section — ${data.charges.length} rows shown`}
      >
        <div>
          <h2 id="charges-heading">Charges</h2>
          <p>
            CUR line items under the active filters. Sorting re-queries Athena,
            so the top rows are the real top rows — not just the ones already
            loaded. Account IDs are masked.
          </p>
        </div>
        <div className="card__header-actions">
          <span className="badge badge--success">Read only</span>
          <button
            className="icon-button"
            type="button"
            onClick={onExpand}
            aria-label="Expand Charges table"
            title="Expand"
          >
            <ExpandIcon size={16} />
          </button>
        </div>
      </div>
      <ChargesTable
        charges={data.charges}
        currencyCode={data.currency}
        sort={sort}
        onSort={onSort}
        variant="inline"
      />
      <footer className="card__footer card__footer--split">
        <span>
          Evidence: {data.evidence.source.toUpperCase()} ·{" "}
          {data.evidence.queryIds.length} queries ·{" "}
          {dataSize(data.evidence.scannedBytes)} scanned · about $
          {data.evidence.estimatedQueryCostUsd.toFixed(4)}
          {data.partial ? " · truncated at the row cap" : ""}
        </span>
        <button
          className="button button--ghost button--small"
          type="button"
          onClick={onExport}
          disabled={data.charges.length === 0}
        >
          Export CSV
        </button>
      </footer>
    </section>
  );
}
